import React, { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import Link from 'next/link';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { Avatar } from '@components/basics/Images/Avatar';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserActivityDetailFragmentDoc } from '@graphql/reactQuery';
import { useHover } from '@hooks/useHover';
import { getActivityTimestamp } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

export function FeedActivityItem({
  children,
  userFrag,
  description,
  timestamp,
  dropdownContent,
}: PropsWithChildren<{
  userFrag: FragmentType<UserActivityDetailFragmentDoc>;
  description: string;
  timestamp: number;
  dropdownContent: React.ReactNode;
}>) {
  const user = getFragment(UserActivityDetailFragmentDoc, userFrag);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [hovering, hoverProps] = useHover();
  const { username, artist, avatar, publicAddress, webappUri } = user;
  const timestampText = getActivityTimestamp(timestamp);
  const { tablet } = MediaQuery.useContainer();

  const userHoverCardContent = useMemo(() => <UserHoverCardContent userId={user.id} />, [user.id]);

  return (
    <div className="flex flex-col" {...hoverProps} data-feed-clickable-div>
      {/**
       * User detail section
       */}
      <div className="flex flex-00auto items-center gap-2 md:gap-4" data-feed-clickable-div>
        {/**
         * Avatar
         */}
        <UserHoverCard
          trigger={
            <div>
              <Avatar
                src={avatar?.url}
                username={username}
                size={tablet ? 48 : 40}
                borderRadius={999}
                walletAddress={publicAddress}
                hasBorder
                webappUri={webappUri}
              />
            </div>
          }
          userHoverCardContent={userHoverCardContent}
        />

        {/**
         * Username, timestamp, description
         */}
        <div className="flex flex-col gap-0.5 ">
          <div className="self-start">
            <UserHoverCard
              trigger={
                <Link href={webappUriPath(webappUri)}>
                  <div className="flex gap-1">
                    <div className="font-base text-base-l font-semibold text-base800">
                      {username}
                    </div>
                    {!!artist && <VerifiedTooltip portal />}
                  </div>
                </Link>
              }
              userHoverCardContent={userHoverCardContent}
            />
          </div>

          <div
            className="flex items-center self-start font-base text-base-m font-medium text-neutral500 lg:h-5"
            data-feed-clickable-div
          >
            <p data-feed-clickable-div>{description}</p>
            <DateTimeTooltip dateTime={new Date(timestamp)}>
              <span className="timestamp">&nbsp;• {timestampText}</span>
            </DateTimeTooltip>
          </div>
        </div>

        <div className="flex-1" data-feed-clickable-div />

        {/**
         * Optional dropdown
         */}
        {dropdownContent && (
          <div className={`${Boolean(hovering || isDropdownOpen) ? `md:block` : `md:hidden`}`}>
            <ThreeDotsDropdown
              setIsDropdownOpen={setIsDropdownOpen}
              isDropdownOpen={isDropdownOpen}
              dropdownProps={{
                align: 'end',
                queue: true,
              }}
            >
              {dropdownContent}
            </ThreeDotsDropdown>
          </div>
        )}
      </div>

      {/**
       * Actual content
       * Laptop has 66px left padding because 48 for avatar + 16 for gap + 2 for border
       */}
      <div className="w-full pt-4 lg:pl-[66px]" data-feed-clickable-div>
        {children}
      </div>
    </div>
  );
}
